import { motion } from "framer-motion";
import { Stethoscope, UserCheck, FileText, FilePlus2 } from "lucide-react";

const STEPS = [
  { icon: Stethoscope, color: "#3B82F6", title: "Esame assistito", desc: "L’infermiere raccoglie i dati con TytoCare." },
  { icon: UserCheck, color: "#FF8A5B", title: "Valutazione del medico", desc: "Il medico analizza le informazioni in diretta." },
  { icon: FileText, color: "#10B981", title: "Referto digitale", desc: "Le conclusioni vengono messe per iscritto." },
  { icon: FilePlus2, color: "#5B2C91", title: "Prescrizione o approfondimento", desc: "Quando appropriati, secondo il percorso clinico." },
];

export default function ReportSection() {
  return (
    <section id="referto" className="bg-white py-20 sm:py-28" data-testid="report-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-6"
          >
            <div className="overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] bg-[#F6F4FA] shadow-[0_30px_80px_rgba(91,44,145,0.18)] ring-1 ring-white/60">
              <img
                src="/generated/referto-consulto.png"
                alt="Kit diagnostico TytoCare aperto sul tavolo, medico che saluta sullo smartphone con etichette Diagnosi e Prescrizione"
                className="w-full h-[340px] sm:h-[440px] object-cover"
                data-testid="report-image"
              />
            </div>
          </motion.div>

          {/* Copy + steps */}
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#F6F4FA] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
            >
              <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
              Dalla visita al referto
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
              data-testid="report-title"
            >
              Dalla visita alle indicazioni cliniche
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-5 text-base leading-relaxed text-[#4A4059]"
            >
              Il medico conduce la visita in collegamento diretto, valuta le informazioni raccolte
              dall’infermiere e conclude la sessione secondo il percorso clinico previsto.
            </motion.p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl bg-white p-4 ring-1 ring-[#5B2C91]/8"
                    data-testid="report-step"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${s.color}18`, color: s.color }}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="font-heading text-sm font-semibold leading-snug text-[#1F152E]">
                        {s.title}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#6B6280]">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
